/**
 * Timeline percentile multiplier with multiple pauses between 0 and 1
 *
 * @param {number} t - Input value between 0 and 1
 * @param {Array<Object>} pauses - Array of pause objects, each with:
 *                               - position: where to pause (0-1)
 *                               - duration: relative duration of pause
 * @param {number} easeStrength - How strong the easing should be (default: 2)
 * @returns {number} Transformed value between 0 and 1
 */
export default function timelineModifierWithMultiplePauses(t, pauses = [{position: 0.5, duration: 0.3}], easeStrength = 2) {
  // Validate input
  if (t < 0) return 0;
  if (t > 1) return 1;

  // Sort pauses by position (just to be safe)
  pauses = pauses.slice().sort((a, b) => a.position - b.position);

  // Calculate total pause duration
  const totalPauseDuration = pauses.reduce((sum, pause) => sum + pause.duration, 0);

  // Validate pauses don't exceed timeline
  if (totalPauseDuration >= 1) {
    console.warn("Total pause duration exceeds or equals 1, adjusting pauses");
    const scaleFactor = 0.95 / totalPauseDuration;
    pauses.forEach(pause => pause.duration *= scaleFactor);
  }

  // Create segments between pauses
  const segments = [];
  let lastPosition = 0;

  // Add segments and pauses
  for (const pause of pauses) {
    // Add moving segment before pause
    if (pause.position > lastPosition) {
      segments.push({
        type: 'move',
        start: lastPosition,
        end: pause.position,
        duration: pause.position - lastPosition
      });
    }

    // Add pause segment
    segments.push({
      type: 'pause',
      position: pause.position,
      duration: pause.duration
    });

    lastPosition = pause.position;
  }

  // Add final segment if needed
  if (lastPosition < 1) {
    segments.push({
      type: 'move',
      start: lastPosition,
      end: 1,
      duration: 1 - lastPosition
    });
  }

  // Calculate actual segment durations considering pauses
  const moveDurationSum = segments
    .filter(seg => seg.type === 'move')
    .reduce((sum, seg) => sum + seg.duration, 0);

  const moveTimeScale = (1 - totalPauseDuration) / moveDurationSum;

  // Convert segment durations to timeline positions
  let timePosition = 0;
  for (const segment of segments) {
    segment.timeStart = timePosition;

    if (segment.type === 'move') {
      timePosition += segment.duration * moveTimeScale;
    } else { // pause
      timePosition += segment.duration;
    }

    segment.timeEnd = timePosition;
  }

  // Find which segment we're in
  let currentSegment = segments.find(seg => t >= seg.timeStart && t < seg.timeEnd);

  // Handle edge case at t=1
  if (t === 1) {
    currentSegment = segments[segments.length - 1];
  }

  // Process based on segment type
  if (currentSegment.type === 'pause') {
    return currentSegment.position;
  } else { // move segment
    const segmentProgress = (t - currentSegment.timeStart) /
                           (currentSegment.timeEnd - currentSegment.timeStart);

    // Apply easing
    const easedProgress = Math.pow(segmentProgress, easeStrength);

    // Calculate position
    return currentSegment.start +
           (currentSegment.end - currentSegment.start) * easedProgress;
  }
}
